import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { authAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Trophy, Crown, Flame, Star, ChevronRight, 
  Calendar, TrendingUp, Award, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const [user, setUser] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [userEntry, setUserEntry] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [timeframe, setTimeframe] = useState('all_time');
  const [loading, setLoading] = useState(true);
  const [annualChallenge, setAnnualChallenge] = useState(null);
  const [showAnnualChallenge, setShowAnnualChallenge] = useState(false);

  // Get translations
  const tl = t.leaderboard || {};
  const tNav = t.nav || {};

  // Get token from localStorage
  const token = localStorage.getItem('spirify_token');

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (user) {
      fetchLeaderboard();
      fetchAnnualChallenge();
    }
  }, [timeframe, user]);

  const fetchUserData = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    }
  };

  const fetchLeaderboard = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/leaderboard?timeframe=${timeframe}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      setLeaderboard(data.entries || []);
      setUserRank(data.user_rank);
      setUserEntry(data.user_entry);
      setTotalUsers(data.total_users || 0);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error(t.common?.error || 'Error loading');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnualChallenge = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API}/api/leaderboard/annual-challenge`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAnnualChallenge(data);
    } catch (error) {
      console.error('Error fetching annual challenge:', error);
    }
  };

  const getRankDisplay = (rank) => {
    if (rank === 1) return { icon: '🥇', color: 'text-yellow-400', bg: 'bg-yellow-500/10' };
    if (rank === 2) return { icon: '🥈', color: 'text-gray-300', bg: 'bg-gray-500/10' };
    if (rank === 3) return { icon: '🥉', color: 'text-amber-600', bg: 'bg-amber-500/10' };
    return { icon: rank, color: 'text-slate-400', bg: '' };
  };

  const getTimeframeLabel = () => {
    if (timeframe === 'weekly') return tl.weeklyTitle || 'Weekly leaderboard';
    if (timeframe === 'monthly') return tl.monthlyTitle || 'Monthly leaderboard';
    return tl.allTimeTitle || 'All-time leaderboard';
  };

  const getXPLabel = () => {
    if (timeframe === 'weekly') return tl.xpWeek || 'XP week';
    if (timeframe === 'monthly') return tl.xpMonth || 'XP month';
    return tl.xpTotal || 'XP total';
  };

  // Timeframe options with translations
  const timeframeOptions = [
    { id: 'weekly', label: tl.weekly || 'Week', icon: Calendar },
    { id: 'monthly', label: tl.monthly || 'Month', icon: TrendingUp },
    { id: 'all_time', label: tl.allTime || 'All time', icon: Trophy }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button 
            onClick={() => navigate('/dashboard')}
            className="text-cyan-400 hover:text-cyan-300 mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {tNav.back || 'Back'}
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
                <Trophy className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{tl.title || 'Leaderboard'}</h1>
                <p className="text-slate-400 text-sm">{tl.subtitle || 'Progress every day and climb the rankings.'}</p>
              </div>
            </div>
            
            {/* Annual Challenge Button */}
            <Button
              variant="outline"
              onClick={() => setShowAnnualChallenge(true)}
              className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
            >
              <Award className="w-4 h-4 mr-2" />
              {tl.annualChallenge || 'Annual Challenge'}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* User Rank Card */}
        {userRank && (
          <Card className="bg-gradient-to-r from-cyan-600/20 to-purple-600/20 border-cyan-500/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-4xl">{user?.avatar || '🧑'}</div>
                  <div>
                    <p className="text-slate-400 text-sm">{tl.yourRank || 'Your rank'}</p>
                    <p className="text-3xl font-bold text-white">
                      #{userRank}
                      <span className="text-slate-400 text-lg font-normal"> / {totalUsers}</span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-slate-400 text-sm">{getXPLabel()}</p>
                  <p className="text-2xl font-bold text-cyan-400">
                    {userEntry?.xp || user?.xp || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Timeframe Tabs */}
        <div className="flex gap-2 mb-6">
          {timeframeOptions.map(tf => (
            <Button
              key={tf.id}
              variant={timeframe === tf.id ? 'default' : 'outline'}
              onClick={() => setTimeframe(tf.id)}
              className={timeframe === tf.id 
                ? 'bg-cyan-600 hover:bg-cyan-700' 
                : 'border-slate-600 text-slate-400 hover:text-white'
              }
            >
              <tf.icon className="w-4 h-4 mr-2" />
              {tf.label}
            </Button>
          ))}
        </div>

        {/* Leaderboard */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white">
              {getTimeframeLabel()}
            </CardTitle>
            <p className="text-slate-400 text-sm">{tl.topPlayers || 'Top 50 learners'}</p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-400">{t.common?.loading || 'Loading...'}</div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                {tl.noData || 'No leaderboard data available for this period.'}
              </div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((entry) => {
                  const rankStyle = getRankDisplay(entry.rank);
                  const isCurrentUser = entry.user_id === user?.id;
                  
                  return (
                    <div 
                      key={entry.user_id}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                        isCurrentUser 
                          ? 'bg-cyan-500/20 border border-cyan-500/30' 
                          : rankStyle.bg || 'hover:bg-slate-700/50'
                      }`}
                    >
                      {/* Rank */}
                      <div className={`w-10 text-center font-bold text-lg ${rankStyle.color}`}>
                        {typeof rankStyle.icon === 'string' ? rankStyle.icon : `#${entry.rank}`}
                      </div>
                      
                      {/* Avatar with Crown for Annual Badge */}
                      <div className="relative">
                        <span className="text-3xl">{entry.avatar || '🧑'}</span>
                        {entry.has_annual_badge && (
                          <div className="absolute -top-2 -right-2 text-yellow-400">
                            <Crown className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                      
                      {/* Name */}
                      <div className="flex-1">
                        <span className={`font-medium ${isCurrentUser ? 'text-cyan-400' : 'text-white'}`}>
                          {entry.username}
                        </span>
                        {isCurrentUser && (
                          <span className="ml-2 text-xs text-cyan-400/70">({tl.you || 'you'})</span>
                        )}
                        {entry.has_annual_badge && (
                          <Badge className="ml-2 bg-yellow-500/20 text-yellow-400 text-xs">
                            🏆 Top 3 {entry.annual_badge_year}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1 text-orange-400">
                          <Flame className="w-4 h-4" />
                          <span className="text-sm">{entry.streak}</span>
                        </div>
                        <div className={`font-bold ${rankStyle.color !== 'text-slate-400' ? rankStyle.color : 'text-cyan-400'}`}>
                          {entry.xp} XP
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Show user entry if not in top 50 */}
                {userEntry && !leaderboard.find(e => e.user_id === user?.id) && (
                  <>
                    <div className="text-center py-2 text-slate-500">• • •</div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-cyan-500/20 border border-cyan-500/30">
                      <div className="w-10 text-center font-bold text-lg text-slate-400">
                        #{userEntry.rank}
                      </div>
                      <span className="text-3xl">{userEntry.avatar || '🧑'}</span>
                      <div className="flex-1">
                        <span className="font-medium text-cyan-400">{userEntry.username}</span>
                        <span className="ml-2 text-xs text-cyan-400/70">({tl.you || 'you'})</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1 text-orange-400">
                          <Flame className="w-4 h-4" />
                          <span className="text-sm">{userEntry.streak}</span>
                        </div>
                        <div className="font-bold text-cyan-400">
                          {userEntry.xp} XP
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Annual Challenge Modal */}
      {showAnnualChallenge && annualChallenge && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  {tl.annualChallenge || 'Annual Challenge'} {annualChallenge.year}
                </CardTitle>
                <Badge className="bg-yellow-500/20 text-yellow-400">{tl.annual || 'Annual'}</Badge>
              </div>
              <p className="text-slate-400 text-sm">{tl.annualDescription || annualChallenge.description}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Top 3 */}
              <div>
                <h4 className="text-sm text-slate-400 mb-3">{tl.currentPodium || 'Current podium'}</h4>
                <div className="space-y-2">
                  {annualChallenge.current_top3?.map((winner) => (
                    <div 
                      key={winner.user_id}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        winner.rank === 1 ? 'bg-yellow-500/10' : 
                        winner.rank === 2 ? 'bg-gray-500/10' : 'bg-amber-500/10'
                      }`}
                    >
                      <span className="text-2xl">{winner.medal}</span>
                      <span className="text-2xl">{winner.avatar}</span>
                      <div className="flex-1">
                        <span className={`font-medium ${
                          winner.rank === 1 ? 'text-yellow-400' : 
                          winner.rank === 2 ? 'text-gray-300' : 'text-amber-500'
                        }`}>
                          {winner.username}
                        </span>
                        {winner.user_id === user?.id && (
                          <span className="ml-2 text-xs text-cyan-400">({tl.you || 'you'}!)</span>
                        )}
                      </div>
                      <span className="font-bold text-cyan-400">{winner.xp} XP</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Prize Info */}
              <div className="bg-slate-700/30 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-2">🎁 {tl.rewards || 'Rewards'}</h4>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>• {tl.specialBadge || 'Special badge'}: {annualChallenge.prize_info?.badge}</li>
                  <li>• {tl.crownOnAvatar || 'Crown on avatar for the following year'}</li>
                </ul>
              </div>

              {/* User Status */}
              {annualChallenge.user_in_top3 ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                  <p className="text-green-400 font-medium">
                    🎉 {tl.youAreTop || "You're in the Top 3!"} #{annualChallenge.user_position}
                  </p>
                </div>
              ) : (
                <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                  <p className="text-slate-400 text-sm">
                    {tl.keepLearning || 'Keep learning to join the podium!'}
                  </p>
                </div>
              )}

              <Button 
                onClick={() => setShowAnnualChallenge(false)}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
              >
                {tl.close || 'Close'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LeaderboardPage;
